import { useTranslation } from "react-i18next";
import { PublicSchedule, Schedule, standardizeWorkingHoursIntervals, WorkingHoursInterval } from "../../utils/schedule";
import { Account } from "../AccountCard";
import { useForm } from "@mantine/form";
import { useRef, useState } from "react";
import { API, HOST, PUBLIC_URL } from "../../utils/const";
import { authFetch } from "../../utils/session";
import { createToast } from "../Toast";
import { CalendarProvider, MessageKind, refactorCalendarName, withLoading } from "../../utils/common";
import { Select, MultiSelect, Stack, Text, TextInput, Group, Button, NumberInput, Checkbox, TagsInput, ActionIcon, Textarea, HoverCard, Badge, Anchor, Paper } from "@mantine/core";
import { DateInput } from '@mantine/dates';
import dayjs from "dayjs";
import { IconPlus, IconTrash, IconCopy, IconInfoCircle, IconUser, IconSpeakerphone } from '@tabler/icons-react';
import { trackFeatureInterest } from "../../utils/analytics";

interface ScheduleFormProps {
    close: () => void
    schedule: Schedule | null
    accounts: Account[]
    successCallback: () => void
}

function toDateStringForInput(v: Date | string | ''): string | null {
    if (v === '' || v == null) return null;
    return dayjs(v).format('YYYY-MM-DD');
}

const SCHEDULE_FORM_FIELD_ORDER = [
    'name',
    'description',
    'email_text',
    'source_calendar_uuid',
    'max_length',
    'interval',
    'start',
    'end',
    'attendee_emails',
    'include_google_meet_link',
    'working_hours_intervals',
] as const;

function scrollElementIntoView(element: HTMLElement) {
    let parent = element.parentElement;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (/(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
            const parentRect = parent.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            parent.scrollTo({
                top: parent.scrollTop + elementRect.top - parentRect.top - parent.clientHeight / 2 + elementRect.height / 2,
                behavior: 'smooth',
            });
            return;
        }
        parent = parent.parentElement;
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function scrollToFirstFormError(
    formElement: HTMLFormElement | null,
    errors: Partial<Record<typeof SCHEDULE_FORM_FIELD_ORDER[number], React.ReactNode>>,
) {
    if (!formElement) {
        return;
    }

    const scrollToField = (field: string) => {
        const target = formElement.querySelector(`[data-form-field="${field}"]`);
        if (!(target instanceof HTMLElement)) {
            return;
        }
        scrollElementIntoView(target);
        const focusable = target.querySelector('input, textarea, [role="combobox"]');
        if (focusable instanceof HTMLElement) {
            focusable.focus({ preventScroll: true });
        }
    };

    for (const field of SCHEDULE_FORM_FIELD_ORDER) {
        if (errors[field]) {
            scrollToField(field);
            return;
        }
    }

    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey) {
        scrollToField(firstErrorKey.split('.')[0]);
    }
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({ close, schedule, accounts, successCallback }) => {
    const { t } = useTranslation(['app']);
    const [loading, setLoading] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const cachedForm = JSON.parse(localStorage.getItem('cachedForm') || '{}');
    const [userTimezone, setUserTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)

    const workingHoursIntervals = schedule?.working_hours_intervals ? standardizeWorkingHoursIntervals(schedule.working_hours_intervals, userTimezone, false) : null

    const form = useForm({
        initialValues: {
            source_calendar_uuid: schedule?.source_calendar_uuid || cachedForm.source_calendar_uuid || '',
            availability_calendar_uuids: schedule?.availability_calendar_uuids || cachedForm.availability_calendar_uuids || [],
            name: schedule?.name || cachedForm.name || '',
            description: schedule?.description || cachedForm.description || '',
            email_text: schedule?.email_text || cachedForm.email_text || '',
            start: schedule?.start || (cachedForm.start ? new Date(cachedForm.start) : '') || '',
            end: schedule?.end || (cachedForm.end ? new Date(cachedForm.end) : '') || '',
            max_number_of_days_in_advance: schedule?.max_number_of_days_in_advance || cachedForm.max_number_of_days_in_advance || undefined,
            max_length: schedule?.max_event_length || cachedForm.max_length || undefined,
            interval: schedule?.interval || cachedForm.interval || schedule?.max_event_length || undefined,
            allow_rescheduling: schedule?.allow_rescheduling || cachedForm.allow_rescheduling || true,
            allow_cancellation: schedule?.allow_cancellation || cachedForm.allow_cancellation || true,
            attendee_emails: schedule?.attendee_emails || cachedForm.attendee_emails || [],
            working_hours_intervals: workingHoursIntervals || cachedForm.working_hours_intervals || [],
            include_google_meet_link: schedule?.conference_settings != null || cachedForm.include_google_meet_link || false,
            min_number_of_hours_in_advance: schedule?.min_number_of_hours_in_advance || cachedForm.min_number_of_hours_in_advance || 0,
        },
        validate: {
            source_calendar_uuid: (value) => (!value ? t('dashboard.required-field') : null),
            name: (value) => (!value ? t('dashboard.required-field') : null),
            description: (value) => (!value ? t('dashboard.required-field') : null),
            start: (value) => (!value ? t('dashboard.required-field') : null),
            max_length: (value) => (!value ? t('dashboard.required-field') : null),
            interval: (value, values) => {
                if (value == null) {
                    return t('dashboard.required-field');
                }
                if (values.max_length && values.max_length % value !== 0) {
                    return t('schedule.modal.interval-must-divide-event-length');
                }
                return null;
            },
            attendee_emails: (value) => (value.length === 0 ? t('dashboard.required-field') : null),
            include_google_meet_link: (value) => (value == null ? t('dashboard.required-field') : null),
            working_hours_intervals: (intervals) => {
                if (intervals.length === 0) {
                    createToast("There must be at least one working hour interval", MessageKind.Error);
                    return t('dashboard.required-field');
                }
                for (const interval of intervals) {
                    if (!interval.day || !interval.start || !interval.end) {
                        return t('dashboard.required-field');
                    }

                    // Convert times to comparable format (minutes since midnight)
                    const [startHours, startMinutes] = interval.start.split(':').map(Number);
                    const [endHours, endMinutes] = interval.end.split(':').map(Number);
                    const startMinutesSinceMidnight = startHours * 60 + startMinutes;
                    const endMinutesSinceMidnight = endHours * 60 + endMinutes;

                    if (startMinutesSinceMidnight >= endMinutesSinceMidnight) {
                        return t('schedule.modal.invalid-time-range');
                    }
                }
                return null;
            },
            email_text: (value) => {
                if (value.length == 0) {
                    return null;
                }
                const meet_link = form.getValues().include_google_meet_link
                if (meet_link && value.indexOf('{{meet_information}}') === -1) {
                    return t('dashboard.must-include-google-meet-information');
                }
                if (value.indexOf('{{reschedule_link}}') === -1) {
                    return t('dashboard.must-include-reschedule-link');
                }
                return null;
            }
        },
        onValuesChange: (values) => {
            localStorage.setItem('cachedForm', JSON.stringify(values));
        },
    });

    const handleSubmit = withLoading(setLoading)(async (values: typeof form.values) => {

        const standardizedWorkingHours = standardizeWorkingHoursIntervals(values.working_hours_intervals, userTimezone, true);
        try {
            let content: Record<string, any> = {
                source_calendar_uuid: values.source_calendar_uuid,
                availability_calendar_uuids: (values.availability_calendar_uuids || []).filter((uuid: string) => uuid !== values.source_calendar_uuid),
                name: values.name,
                description: values.description,
                email_text: values.email_text,
                start: dayjs(values.start).format('YYYY-MM-DDTHH:mm:ss'),
                end: values.end ? dayjs(values.end).format('YYYY-MM-DDTHH:mm:ss') : null,
                min_number_of_hours_in_advance: values.min_number_of_hours_in_advance || 0,
                max_number_of_days_in_advance: values.max_number_of_days_in_advance || 365,
                max_event_length: values.max_length,
                interval: values.interval,
                attendee_emails: values.attendee_emails,
                allow_rescheduling: values.allow_rescheduling,
                allow_cancellation: values.allow_cancellation,
                working_hours_intervals: standardizedWorkingHours,
                conference_settings: values.include_google_meet_link ? {
                    provider: "google_meet",
                } : null,
            }
            let endpoint = `${API}/schedule`
            if (schedule != null) {
                endpoint = `${API}/schedule/${schedule.uuid}`
            }

            const response = await authFetch(endpoint, {
                method: schedule ? 'PATCH' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(content),
            });

            const body = await response.json();
            if (!response.ok) {
                createToast(body.detail || 'Error while saving creating schedule', MessageKind.Error);
                return;
            }
            localStorage.removeItem('cachedForm');
            successCallback();
            close();
        } catch (error) {
            console.error('Error creating schedule:', error);
            createToast('An unexpected error occurred', MessageKind.Error);
        }
    });

    let sourceCalendars = []
    if (accounts) {
        let primaryCalendars = []
        for (let account of accounts) {
            if (account.provider !== CalendarProvider.GOOGLE) {
                continue;
            }
            for (let calendar of account.calendars ?? []) {
                if (calendar.primary) {
                    primaryCalendars.push(calendar.platform_id)
                }
            }
        }

        sourceCalendars = accounts.filter(a => a.provider === CalendarProvider.GOOGLE).map(a => ({
            group: a.key,
            items: (
                a.calendars?.filter(
                    c => c.primary || !primaryCalendars.includes(c.platform_id)
                ).map(
                    c => ({ value: c.uuid, label: refactorCalendarName(c.name), selected: c.uuid === schedule?.source_calendar_uuid })
                ) || []
            )
        }));
    }

    // Additional calendars to check availability against (any Google calendar except the chosen source)
    let availabilityCalendars: { group: string, items: { value: string, label: string }[] }[] = []
    if (accounts) {
        availabilityCalendars = accounts.filter(a => a.provider === CalendarProvider.GOOGLE).map(a => ({
            group: a.key,
            items: (
                a.calendars?.filter(c => c.uuid !== form.values.source_calendar_uuid).map(
                    c => ({ value: c.uuid, label: refactorCalendarName(c.name) })
                ) || []
            )
        })).filter(g => g.items.length > 0);
    }


    const colors = [
        { id: '0', background: '#ffffff', name: 'Default' },
        { id: '8', background: '#616161', name: 'Graphite' },
        { id: '9', background: '#3F51B5', name: 'Blueberry' },
        { id: '1', background: '#7986CB', name: 'Lavender' },
        { id: '3', background: '#8E24AA', name: 'Grape' },
        { id: '7', background: '#039BE5', name: 'Peacock' },
        { id: '10', background: '#0B8043', name: 'Basil' },
        { id: '2', background: '#33B679', name: 'Sage' },
        { id: '5', background: '#F6BF26', name: 'Banana' },
        { id: '6', background: '#F4511E', name: 'Tangerine' },
        { id: '4', background: '#E67C73', name: 'Flamingo' },
        { id: '11', background: '#D50000', name: 'Tomato' },
    ];

    const preview = () => {
        const values = form.values;

        if (values.source_calendar_uuid === '' || values.name === '' || values.description === '' || values.start === '') {
            createToast('Please fill all the required fields before previewing', MessageKind.Error);
            return;
        }

        const working_hours_intervals = values.working_hours_intervals.map(interval => ({
            day: Number(interval.day),
            start: interval.start,
            end: interval.end,
        }));

        const scheduleData: PublicSchedule = {
            uuid: values.source_calendar_uuid,
            source_calendar_uuid: values.source_calendar_uuid,
            availability_calendar_uuids: (values.availability_calendar_uuids || []).filter(
                (uuid: string) => uuid !== values.source_calendar_uuid,
            ),
            name: values.name,
            description: values.description,
            start: dayjs(values.start).format('YYYY-MM-DDTHH:mm:ss'),
            end: values.end ? dayjs(values.end).format('YYYY-MM-DDTHH:mm:ss') : null,
            min_number_of_hours_in_advance: 0,
            max_number_of_days_in_advance: values.max_number_of_days_in_advance,
            max_event_length: values.max_length,
            interval: values.interval,
            working_hours_intervals: working_hours_intervals,
        };

        const encodedPreview = btoa(JSON.stringify(scheduleData));
        window.open(`${HOST}${PUBLIC_URL}/book?preview=${encodeURIComponent(encodedPreview)}`, '_blank');
    };

    const addWorkingHoursInterval = () => {
        form.insertListItem('working_hours_intervals', {
            day: 1,
            start: '09:00',
            end: '17:00'
        });
    };

    const duplicateWorkingHoursInterval = (index: number) => {
        const intervalToDuplicate = form.values.working_hours_intervals[index];
        form.insertListItem('working_hours_intervals', { ...intervalToDuplicate });
    };

    const voteMultipleCalendars = () => {
        if (localStorage.getItem('vote_multiple_calendars') === 'true') {
            createToast('You have already voted!', MessageKind.Info);
            return;
        }
        trackFeatureInterest('multiple_calendars');
        createToast('Thank you for your vote!', MessageKind.Success);
        localStorage.setItem('vote_multiple_calendars', 'true');
    };

    const getCalendarOwnerEmail = (calendarUuid: string) => {
        const account = accounts.find(a =>
            a.calendars?.some(c => c.uuid === calendarUuid)
        );
        return account?.key || '';
    };

    // Add helper function to get all account emails
    const getAllAccountEmails = () => {
        return accounts.map(account => account.key);
    };

    return (
        <form ref={formRef} onSubmit={form.onSubmit(handleSubmit, (errors) => {
            requestAnimationFrame(() => {
                scrollToFirstFormError(formRef.current, errors);
            });
        })}>
            <Stack gap="sm">
                <TextInput
                    label={t("schedule.modal.name")}
                    placeholder={t("schedule.modal.name")}
                    data-form-field="name"
                    {...form.getInputProps('name')}
                    required
                />
                <TextInput
                    label={t("schedule.modal.description")}
                    placeholder={t("schedule.modal.description")}
                    data-form-field="description"
                    {...form.getInputProps('description')}
                    required
                />
                <Textarea
                    label={t("schedule.modal.email-label")}
                    placeholder={`[${t('schedule.modal.optional')}] ${t("schedule.modal.email-text")}`}
                    data-form-field="email_text"
                    rightSection={<HoverCard width={320} shadow="md"><HoverCard.Target><IconInfoCircle size={16} /></HoverCard.Target><HoverCard.Dropdown>
                        <Text fz="sm">{t("schedule.modal.email-text")}</Text>
                    </HoverCard.Dropdown></HoverCard>}
                    {...form.getInputProps('email_text')}
                />
                {/* <Paper radius="md" p={'4'} bg="primary.0">
                    <Text c='primary.9' fz='xs' fw='bold'><IconUser size={14} /> Your Feedback is important to us</Text>
                    <Text c='primary.9' fz='xs'>Currently, only Google Calendar are supported as source calendar. Want to see Outlook? Click <Anchor fz='xs' onClick={voteSourceOutlook}>here</Anchor> to vote</Text>
                </Paper> */}
                <Select
                    label={t("dashboard.sync.draft.source") + ' [Google]'}
                    placeholder={t("dashboard.sync.draft.source")}
                    data-form-field="source_calendar_uuid"
                    data={sourceCalendars}
                    {...form.getInputProps('source_calendar_uuid')}
                    searchable
                    value={form.values.source_calendar_uuid}
                    required
                    onChange={(value) => {
                        form.setFieldValue('source_calendar_uuid', value);
                        if (value) {
                            // a calendar can't be both the source and an additional availability calendar
                            form.setFieldValue('availability_calendar_uuids', (form.values.availability_calendar_uuids || []).filter((uuid: string) => uuid !== value));
                            const ownerEmail = getCalendarOwnerEmail(value);
                            const accountEmails = getAllAccountEmails();
                            // Keep only non-account emails and add new owner email at start
                            const otherEmails = form.values.attendee_emails.filter(email =>
                                !accountEmails.includes(email)
                            );
                            form.setFieldValue('attendee_emails', [ownerEmail, ...otherEmails]);
                        }
                    }}
                />
                <MultiSelect
                    label={t('schedule.modal.availability-calendars')}
                    placeholder={t('schedule.modal.availability-calendars-placeholder')}
                    description={t('schedule.modal.availability-calendars-description')}
                    data={availabilityCalendars}
                    searchable
                    clearable
                    {...form.getInputProps('availability_calendar_uuids')}
                />
                <Text size='lg' c='dimmed'>{t('schedule.modal.set-dates-and-length')}</Text>
                <Group>
                    <NumberInput
                        label={t('schedule.modal.event-length')}
                        placeholder={`${t('schedule.modal.event-length')}`}
                        data-form-field="max_length"
                        {...form.getInputProps('max_length')}
                        hideControls
                        onChange={(value) => {
                            const numericValue = typeof value === 'string'
                                ? Number.parseInt(value, 10)
                                : value;
                            const intervalFollowsEventLength =
                                form.values.interval == null
                                || Number(form.values.interval) === Number(form.values.max_length);
                            if (intervalFollowsEventLength && numericValue != null && !Number.isNaN(numericValue)) {
                                form.setFieldValue('interval', numericValue);
                            }
                            form.setFieldValue('max_length', numericValue);
                            form.validateField('interval');
                        }}
                        required
                    />
                    <NumberInput
                        label={t('schedule.modal.interval-between-events')}
                        placeholder={`${t('schedule.modal.interval-between-events')}`}
                        description={t('schedule.modal.interval-between-events-description')}
                        data-form-field="interval"
                        {...form.getInputProps('interval')}
                        hideControls
                        min={1}
                        required
                    />
                </Group>
                <Group>
                    <DateInput
                        label={t('schedule.modal.start-date')}
                        placeholder={t('schedule.modal.start-date')}
                        data-form-field="start"
                        value={toDateStringForInput(form.values.start)}
                        onChange={(val) => form.setFieldValue('start', val ? dayjs(val).toDate() : '')}
                        error={form.errors.start}
                        minDate={dayjs().format('YYYY-MM-DD')}
                        required
                    />
                    <DateInput
                        label={t('schedule.modal.end-date')}
                        placeholder={`[${t('schedule.modal.optional')}] ${t('schedule.modal.end-date')}`}
                        value={toDateStringForInput(form.values.end)}
                        onChange={(val) => form.setFieldValue('end', val ? dayjs(val).toDate() : '')}
                        error={form.errors.end}
                        minDate={
                            form.values.start
                                ? dayjs(form.values.start).format('YYYY-MM-DD')
                                : dayjs().format('YYYY-MM-DD')
                        }
                        clearable
                    />
                </Group>
                <Group>
                    <NumberInput
                        label={t('schedule.modal.min-number-of-hours-in-advance')}
                        placeholder={`[${t('schedule.modal.optional')}] ${t('schedule.modal.min-number-of-hours-in-advance')}`}
                        {...form.getInputProps('min_number_of_hours_in_advance')}
                        hideControls
                        allowDecimal={false}
                    />
                    <NumberInput
                        label={t('schedule.modal.max-number-of-days-in-advance')}
                        placeholder={`[${t('schedule.modal.optional')}] ${t('schedule.modal.max-number-of-days-in-advance')}`}
                        {...form.getInputProps('max_number_of_days_in_advance')}
                        hideControls
                        allowDecimal={false}
                    />
                </Group>
                <Text size='lg' c='dimmed'>{t('schedule.modal.attendees-settings')}</Text>
                <TagsInput
                    label={t('schedule.modal.attendee-emails')}
                    placeholder={t('schedule.modal.attendee-email')}
                    data-form-field="attendee_emails"
                    {...form.getInputProps('attendee_emails')}
                    onChange={(values) => {
                        if (form.values.source_calendar_uuid) {
                            const ownerEmail = getCalendarOwnerEmail(form.values.source_calendar_uuid);
                            const accountEmails = getAllAccountEmails();
                            // Keep only non-account emails and ensure owner email stays first
                            const otherEmails = values.filter(email =>
                                !accountEmails.includes(email) || email === ownerEmail
                            );
                            form.setFieldValue('attendee_emails', [ownerEmail, ...otherEmails.filter(email => email !== ownerEmail)]);
                        } else {
                            form.setFieldValue('attendee_emails', values);
                        }
                    }}
                />
                <Checkbox
                    label={t('schedule.modal.include-google-meet-link')}
                    checked={form.values.include_google_meet_link}
                    data-form-field="include_google_meet_link"
                    {...form.getInputProps('include_google_meet_link')}
                />
                {/* <Checkbox
                    label={t('schedule.modal.allow-rescheduling')}
                    checked={form.values.allow_rescheduling}
                    {...form.getInputProps('allow_rescheduling')}
                />
                <Checkbox
                    label={t('schedule.modal.allow-cancellation')}
                    checked={form.values.allow_cancellation}
                    {...form.getInputProps('allow_cancellation')}
                /> */}
                <Group justify="space-between">
                    <Text size='lg' c='dimmed'>{t('schedule.modal.working-hours')}</Text>
                    <Select
                        placeholder={t('schedule.modal.select-timezone')}
                        data={Intl.supportedValuesOf('timeZone').map(tz => ({
                            value: tz,
                            label: tz.replace(/_/g, ' ')
                        }))}
                        searchable
                        value={userTimezone}
                        onChange={(value) => {
                            setUserTimezone(value);
                        }}
                    />
                </Group>
                {form.values.working_hours_intervals.map((_, index) => (
                    <Group key={index} gap={"8"} data-form-field={index === 0 ? 'working_hours_intervals' : undefined}>
                        <Select
                            w={200}
                            label={t('schedule.modal.day')}
                            data={[
                                { value: '1', label: t('common.monday') },
                                { value: '2', label: t('common.tuesday') },
                                { value: '3', label: t('common.wednesday') },
                                { value: '4', label: t('common.thursday') },
                                { value: '5', label: t('common.friday') },
                                { value: '6', label: t('common.saturday') },
                                { value: '0', label: t('common.sunday') },
                            ]}
                            {...form.getInputProps(`working_hours_intervals.${index}.day`)}
                            onChange={(value: string) => { form.setFieldValue(`working_hours_intervals.${index}.day`, parseInt(value)) }}
                            value={form.values.working_hours_intervals[index].day.toString()}
                        />
                        <TextInput
                            flex={1}
                            label={t('schedule.modal.start-time')}
                            type="time"
                            {...form.getInputProps(`working_hours_intervals.${index}.start`)}
                        />
                        <TextInput
                            flex={1}
                            label={t('schedule.modal.end-time')}
                            type="time"
                            {...form.getInputProps(`working_hours_intervals.${index}.end`)}
                        />
                        <Group gap={8} mt={24}>
                            <ActionIcon
                                color="blue"
                                onClick={() => duplicateWorkingHoursInterval(index)}
                                title={t('common.duplicate')}
                            >
                                <IconCopy size={16} />
                            </ActionIcon>
                            {form.values.working_hours_intervals.length > 1 && (
                                <ActionIcon
                                    color="red"
                                    onClick={() => form.removeListItem('working_hours_intervals', index)}
                                    title={t('common.delete')}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            )}
                        </Group>
                    </Group>
                ))}
                <Button
                    leftSection={<IconPlus size={14} />}
                    variant="outline"
                    onClick={addWorkingHoursInterval}
                >
                    {t('schedule.modal.add-working-hours')}
                </Button>
            </Stack>
            <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={close}>{t("common.cancel")}</Button>
                <Button variant="outline" onClick={preview}>{t("common.preview")}</Button>
                <Button type="submit" loading={loading}>{t("common.save")}</Button>
            </Group>
        </form >
    );
};

export default ScheduleForm;